<template>
    <kiosk-user-details :userId="props.userId">
        <div class="flex flex-col w-full justify-center">
            <page-header>Logins</page-header>

            <page-loading v-if="loading && !list.length"></page-loading>

            <div v-else-if="list.length">
                <vx-table class="xl:grid-cols-6 mb-8" rowBreakpoint="xl">
                    <vx-table-row isHeader>
                        <div class="lg:pl-6">Time</div>
                        <div>Method</div>
                        <div>Result</div>
                        <div>Team</div>
                        <div>IP Address</div>
                        <div>Browser</div>
                    </vx-table-row>

                    <vx-table-row v-for="login in list" :key="login.id" data-test="login">
                        <vx-table-row-title>
                            {{ shortDateTime(login.created_at) }}
                        </vx-table-row-title>

                        <vx-table-row-item>
                            <template v-slot:label> Method: </template>
                            {{ methodLabel(login.method) }}
                        </vx-table-row-item>

                        <vx-table-row-item>
                            <template v-slot:label> Result: </template>
                            <template v-if="'success' === login.result">Success</template>
                            <vx-popover v-else-if="login.failure_reason" hover arrow placement="top">
                                Failed · {{ failureReasonLabel(login.failure_reason) }}
                                <template v-slot:content>
                                    <code class="text-xs">{{ login.failure_reason }}</code>
                                </template>
                            </vx-popover>
                            <template v-else>Failed</template>
                        </vx-table-row-item>

                        <vx-table-row-item>
                            <template v-slot:label> Team: </template>
                            {{ login.team_name ?? '-' }}
                        </vx-table-row-item>

                        <vx-table-row-item>
                            <template v-slot:label> IP Address: </template>
                            <vx-popover v-if="login.ip_address" hover arrow placement="top" class="w-full">
                                <button
                                    type="button"
                                    class="block max-w-full text-left focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-blue-500"
                                    data-test="ip-address"
                                >
                                    <span class="block truncate">{{ login.ip_address }}</span>
                                </button>
                                <template v-slot:content>
                                    <code class="text-xs">{{ login.ip_address }}</code>
                                </template>
                            </vx-popover>
                            <template v-else>-</template>
                        </vx-table-row-item>

                        <vx-table-row-item>
                            <template v-slot:label> Browser: </template>
                            <vx-popover v-if="login.user_agent" hover arrow placement="top" class="w-full">
                                <button
                                    type="button"
                                    class="block max-w-full text-left focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-blue-500"
                                >
                                    <span class="block truncate">{{ userAgentLabel(login.user_agent) }}</span>
                                </button>
                                <template v-slot:content>
                                    <div class="max-w-xs break-all">{{ login.user_agent }}</div>
                                </template>
                            </vx-popover>
                            <template v-else>-</template>
                        </vx-table-row-item>
                    </vx-table-row>
                </vx-table>

                <div class="mt-8 flex justify-between w-full gap-10">
                    <vx-button
                        v-if="fetchParams.cursor"
                        @click="loadData()"
                        color="muted-dark"
                        size="xl"
                        class="grow-0"
                        data-test="load-more-btn"
                        :loading="loading"
                    >
                        Load More
                        <font-awesome-icon widthAuto :icon="faSpinner"></font-awesome-icon>
                    </vx-button>
                </div>
            </div>

            <empty-state v-else> No logins yet. </empty-state>
        </div>
    </kiosk-user-details>
</template>

<script setup>
import KioskUserDetails from './KioskUserDetails.vue';
import PageHeader from '~/components/general/PageHeader.vue';
import PageLoading from '~/components/general/PageLoading.vue';
import EmptyState from '~/components/general/EmptyState.vue';
import { adminUsersClient } from '~/services/index';
import { useCursorPagination } from '~/composables/useCursorPagination';
import { faSpinner } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxButton, VxPopover, VxTable, VxTableRow, VxTableRowItem, VxTableRowTitle } from '@voxie/frontend-components';
import { shortDateTime } from '~/utils/date';
import { userAgentLabel } from '~/utils/userAgent';
import { failureReasonLabel, methodLabel } from '~/components/kiosk/kiosk-teams/sso/kioskTeamSsoLoginsTableHelpers';

const props = defineProps({
    userId: {
        type: Number,
        required: true,
    },
});

const { loading, list, fetchParams, fetchRecords } = useCursorPagination();

const loadData = () => {
    fetchRecords(adminUsersClient.getUserLogins(props.userId, fetchParams.value));
};

fetchParams.value = { cursor: null };
loadData();
</script>
